import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { leadsApi } from "../api/leads.js";
import {
  Phone,
  Mail,
  ArrowLeft,
  Smartphone,
  MapPin,
  Store,
  Hash,
  Package,
  FileText,
  CheckCircle,
  User,
} from "lucide-react";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const Badge = ({ label }) => {
  if (!label) return null;
  const labelLower = label.toLowerCase();
  let bg = "#f1f5f9";
  let color = "#475569";
  let border = "#e2e8f0";

  if (labelLower === "delivered") {
    bg = "#dcfce7";
    color = "#15803d";
    border = "#bbf7d0";
  } else if (labelLower === "cancelled" || labelLower === "canceled") {
    bg = "#fee2e2";
    color = "#dc2626";
    border = "#fecaca";
  } else if (labelLower === "pending" || labelLower === "processing" || labelLower === "dispatched" || labelLower === "shipped") {
    bg = "#dbeafe";
    color = "#1d4ed8";
    border = "#bfdbfe";
  } else if (labelLower === "gold") {
    bg = "#fef3c7";
    color = "#92400e";
    border = "#fcd34d";
  } else if (labelLower.includes("green") || labelLower.includes("active")) {
    bg = "#dcfce7";
    color = "#16a34a";
    border = "#bbf7d0";
  } else if (labelLower === "yellow" || labelLower.includes("average")) {
    bg = "#fef9c3";
    color = "#a16207";
    border = "#fde68a";
  } else if (labelLower.includes("red") || labelLower.includes("at risk") || labelLower.includes("inactive")) {
    bg = "#fee2e2";
    color = "#dc2626";
    border = "#fecaca";
  }

  return (
    <span style={{
      fontSize: "11px",
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: "99px",
      background: bg,
      color: color,
      border: `1.5px solid ${border}`,
      display: "inline-flex",
      alignItems: "center"
    }}>
      {label}
    </span>
  );
};

const SkeletonDetailPage = () => {
  return (
    <div className="page-wrapper" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
          background-size: 400% 100%;
          animation: shimmer 1.4s ease infinite;
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
        <div className="skeleton-shimmer" style={{ width: "120px", height: "20px", borderRadius: "4px" }} />
      </div>
      {/* Header Skeleton */}
      <div className="detail-card" style={{ padding: "24px", marginBottom: "24px", borderRadius: "12px", background: "var(--card)", border: "1.5px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div className="skeleton-shimmer" style={{ width: "48px", height: "48px", borderRadius: "10px" }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton-shimmer" style={{ width: "200px", height: "24px", borderRadius: "6px", marginBottom: "8px" }} />
            <div className="skeleton-shimmer" style={{ width: "350px", height: "14px", borderRadius: "4px" }} />
          </div>
        </div>
      </div>
      {/* Profile Grid Skeleton */}
      <div className="profile-grid">
        <div className="detail-card" style={{ height: "300px" }}>
          <div className="skeleton-shimmer" style={{ width: "150px", height: "18px", borderRadius: "4px", marginBottom: "16px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ display: "flex", gap: "12px" }}>
                <div className="skeleton-shimmer" style={{ width: "20px", height: "20px", borderRadius: "4px" }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton-shimmer" style={{ width: "60px", height: "10px", borderRadius: "2px", marginBottom: "4px" }} />
                  <div className="skeleton-shimmer" style={{ width: "120px", height: "14px", borderRadius: "3px" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="detail-card" style={{ height: "300px" }}>
          <div className="skeleton-shimmer" style={{ width: "150px", height: "18px", borderRadius: "4px", marginBottom: "16px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            {[...Array(6)].map((_, i) => (
              <div key={i}>
                <div className="skeleton-shimmer" style={{ width: "60px", height: "10px", borderRadius: "2px", marginBottom: "4px" }} />
                <div className="skeleton-shimmer" style={{ width: "100px", height: "16px", borderRadius: "4px" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CallModal = ({ customer, onClose, onSuccess }) => {
  const [agentPhone, setAgentPhone] = useState(
    () => localStorage.getItem("agent_phone_number") || ""
  );
  const [calling, setCalling] = useState(false);

  if (!customer) return null;

  const handleCall = async (e) => {
    e.preventDefault();
    if (!agentPhone.match(/^[6-9]\d{9}$/)) {
      toast.error("Please enter a valid 10-digit agent phone number");
      return;
    }
    setCalling(true);
    try {
      localStorage.setItem("agent_phone_number", agentPhone);
      await leadsApi.triggerCall(customer.id, { agent_phone: agentPhone });
      toast.success("Call initiated successfully!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to initiate call");
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ display: "flex", alignItems: "center", justifyContent: "center", position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ background: "var(--card)", padding: "24px", borderRadius: "12px", border: "1.5px solid var(--border)", width: "400px", maxWidth: "90%" }}>
        <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Initiate Outbound Call</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "var(--text-muted)", padding: 0 }}>✕</button>
        </div>
        <form onSubmit={handleCall}>
          <div style={{ marginBottom: "16px" }}>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "0 0 12px 0" }}>
              Calling <strong>{customer.name}</strong> ({customer.phone})
            </p>
            <label className="form-label" style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: 600 }}>Your (Agent) Phone Number *</label>
            <input
              type="text"
              className="form-control"
              value={agentPhone}
              onChange={(e) => setAgentPhone(e.target.value)}
              placeholder="Enter your 10-digit mobile number"
              maxLength={10}
              required
              autoFocus
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={calling}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={calling}>{calling ? "Calling..." : "Call Now"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const getTimelineIcon = (type) => {
  switch (type) {
    case "order_placed":
      return <Package size={14} style={{ color: "var(--primary)" }} />;
    case "call_logged":
      return <Phone size={14} style={{ color: "var(--success)" }} />;
    case "email_sent":
      return <Mail size={14} style={{ color: "var(--info)" }} />;
    case "account_created":
      return <CheckCircle size={14} style={{ color: "var(--success)" }} />;
    default:
      return <FileText size={14} style={{ color: "var(--text-muted)" }} />;
  }
};

const formatStatusFlag = (flag) => {
  if (!flag) return "Unknown";
  if (flag === "gold" || flag === "green ++") return "Gold";
  if (flag === "blue") return "Blue";
  return flag.charAt(0).toUpperCase() + flag.slice(1);
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callModal, setCallModal] = useState(null);

  const fetchCustomerDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsApi.getProfile(id);
      const response = res.data?.data ?? res.data ?? res;
      if (!response) return;

      const profile = response.profile || {};
      const financial = response.financial_summary || {};
      const primaryLocation = (profile.locations && profile.locations.find(loc => loc.is_primary)) || (profile.locations?.[0]) || {};

      setCustomer({
        id: profile.id,
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        altPhone: profile.phone_2 || "",
        shopName: profile.name || "",
        franchise: profile.product_category || "",
        gst: profile.gst_number || "",
        type: profile.customer_type || "Retail",
        notes: profile.notes || "",
        status: formatStatusFlag(profile.status_flag),
        totalPaid: parseFloat(financial.total_paid || 0),
        creditDue: parseFloat(financial.credit_due || 0),
        lastOrder: financial.last_order_date ? financial.last_order_date.split("T")[0] : "",
        avgOrderGapDays: financial.avg_order_gap_days ?? null,
        createdAt: financial.member_since ? financial.member_since.split("T")[0] : "",
        address: {
          street: primaryLocation.address_line_1 || "",
          building: primaryLocation.address_line_2 || "",
          city: primaryLocation.city || "",
          state: primaryLocation.state || "",
          pinCode: primaryLocation.pincode || "",
        },
      });

      // Always fetch full order list from the dedicated endpoint; fall back to
      // recent_orders from the profile if the orders endpoint fails.
      let ordersRaw = response.recent_orders || [];
      try {
        const ordersRes = await leadsApi.getOrders(id, { limit: 200 });
        const ordersData = ordersRes?.data ?? ordersRes;
        const fetched = Array.isArray(ordersData)
          ? ordersData
          : (Array.isArray(ordersData?.data) ? ordersData.data : ordersData?.rows || []);
        if (fetched.length > 0) ordersRaw = fetched;
      } catch (ordersErr) {
        console.error("[CustomerDetail] orders fetch error:", ordersErr);
      }

      const mapOrder = (order) => {
        const dateStr =
          order.dispatch_date  ||
          order.delivery_date  ||
          order.created_at     ||
          null;
        return {
          id: order.id,
          customerId: order.lead_id,
          customerName: profile.name || "",
          products: (order.items || order.OrderItems || []).map((item) => ({
            productId: item.product_id,
            name: item.product?.name || item.Product?.name || "",
            category: item.product?.category || item.Product?.category || "",
            qty: item.quantity || 0,
            price: parseFloat(item.unit_price || 0),
          })),
          total: parseFloat(order.total_amount || 0),
          status: order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1).toLowerCase()) : "Pending",
          orderDate: dateStr ? dateStr.split("T")[0] : "",
          _raw: order,
        };
      };

      const mappedOrders = ordersRaw
        .map(mapOrder)
        .sort((a, b) => {
          const da = a.orderDate ? new Date(a.orderDate).getTime() : 0;
          const db = b.orderDate ? new Date(b.orderDate).getTime() : 0;
          return db - da;
        });
      setCustomerOrders(mappedOrders);

      // Build a quick lookup: orderId → effective date string (dispatch→delivery→created)
      const orderDateMap = {};
      ordersRaw.forEach(o => {
        const ds = o.dispatch_date || o.delivery_date || o.created_at || null;
        if (ds) orderDateMap[o.id] = ds.split("T")[0];
      });

      // Calculate avg gap between orders using effective dates
      const effectiveDates = ordersRaw
        .map(o => {
          const ds = o.dispatch_date || o.delivery_date || o.created_at;
          return ds ? new Date(ds).getTime() : null;
        })
        .filter(Boolean)
        .sort((a, b) => a - b);

      let computedAvgGap = null;
      if (effectiveDates.length >= 2) {
        let total = 0;
        for (let i = 1; i < effectiveDates.length; i++) {
          total += effectiveDates[i] - effectiveDates[i - 1];
        }
        computedAvgGap = Math.round(total / (effectiveDates.length - 1) / 86400000);
      }

      // Fetch chronological timeline feed
      try {
        const timelineRes = await leadsApi.getTimeline(id);
        const rawTimeline = Array.isArray(timelineRes)
          ? timelineRes
          : (Array.isArray(timelineRes?.data) ? timelineRes.data : timelineRes?.data?.data);
        if (Array.isArray(rawTimeline)) {
          // For order_placed entries, override server timestamp with dispatch→delivery→created
          const fixed = rawTimeline.map(t => {
            if (t.type === "order_placed" && t.details?.order_id) {
              const correctDate = orderDateMap[t.details.order_id];
              if (correctDate) return { ...t, timestamp: correctDate };
            }
            return t;
          });
          setTimelineData(fixed);
        }
      } catch (timelineErr) {
        console.error("[CustomerDetail] timeline fetch error:", timelineErr);
      }

      // Store computed avg gap into customer state
      setCustomer(prev => prev ? { ...prev, avgOrderGapDays: computedAvgGap } : prev);
    } catch (err) {
      console.error("[CustomerDetail] fetch error:", err);
      const isPermissionError = err.response?.status === 403 || err.permissionDenied;
      const msg = isPermissionError
        ? (err.response?.data?.error || err.friendlyMessage || "Permission denied")
        : "Failed to load customer details";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCustomerDetails(); }, [fetchCustomerDetails]);

  if (loading) return <SkeletonDetailPage />;

  if (!customer) {
    return (
      <div className="page-wrapper">
        <div className="empty-state">
          <div className="empty-icon"><User size={48} style={{ color: "var(--text-muted)", opacity: 0.6 }} /></div>
          <h3>Customer not found</h3>
          <button className="btn-primary" onClick={() => navigate("/customers")} style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <ArrowLeft size={16} /> Back to Customers
          </button>
        </div>
      </div>
    );
  }

  const addr = customer.address;
  const addressDisplay =
    typeof addr === "string"
      ? addr
      : [addr?.building, addr?.floor, addr?.street, addr?.city, addr?.state, addr?.pinCode ? `PIN: ${addr.pinCode}` : ""]
        .filter(Boolean).join(", ");

  return (
    <div className="page-wrapper">
      <button className="back-btn" onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <ArrowLeft size={14} /> Back to Customers
      </button>

      {/* Customer Header */}
      <div className="detail-card" style={{ padding: 0, marginBottom: "24px" }}>
        {/* Top accent bar */}
        <div style={{ height: "4px", background: "linear-gradient(90deg, var(--primary), #818cf8)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }} />
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" }}>
            {/* Left: Avatar + Info */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", flex: 1, minWidth: 0 }}>
              <div style={{
                width: "48px", height: "48px", borderRadius: "10px",
                background: "var(--primary)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.15rem", fontWeight: 700, flexShrink: 0,
                letterSpacing: "0.02em"
              }}>
                {customer.name?.[0]?.toUpperCase() || "C"}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "6px" }}>
                  <h1 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text)", margin: 0, lineHeight: 1.3 }}>
                    {customer.name}
                  </h1>
                  <Badge label={customer.status} />
                  <Badge label={customer.type} />
                </div>
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  {customer.phone && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <Phone size={12} strokeWidth={2} /> {customer.phone}
                    </span>
                  )}
                  {customer.email && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <Mail size={12} strokeWidth={2} /> {customer.email}
                    </span>
                  )}
                  {customer.franchise && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <Store size={12} strokeWidth={2} /> {customer.franchise}
                    </span>
                  )}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <Hash size={12} strokeWidth={2} /> ID-{customer.id}
                  </span>
                </div>
              </div>
            </div>
            {/* Right: Actions */}
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setCallModal({ ...customer, callStartTime: Date.now() })}
                className="btn-primary"
                style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 16px", fontSize: "0.85rem", fontWeight: 600, border: "none", cursor: "pointer" }}
              >
                <Phone size={14} strokeWidth={2.5} /> Call
              </button>
              <a href={`mailto:${customer.email}`} className="btn-outline" style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 16px", fontSize: "0.85rem", fontWeight: 600 }}>
                <Mail size={14} strokeWidth={2.5} /> Email
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-grid">
        {/* Contact Info */}
        <div className="detail-card">
          <h3 className="detail-title">Contact Information</h3>
          <div className="detail-list">
            <div className="detail-row">
              <span className="detail-icon" style={{ display: "inline-flex", alignSelf: "center" }}><Phone size={16} style={{ color: "var(--primary)" }} /></span>
              <div><div className="detail-label">Phone</div><div className="detail-val mono">{customer.phone}</div></div>
            </div>
            {customer.altPhone && (
              <div className="detail-row">
                <span className="detail-icon" style={{ display: "inline-flex", alignSelf: "center" }}><Smartphone size={16} style={{ color: "var(--primary)" }} /></span>
                <div><div className="detail-label">Alt Phone</div><div className="detail-val mono">{customer.altPhone}</div></div>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-icon" style={{ display: "inline-flex", alignSelf: "center" }}><Mail size={16} style={{ color: "var(--primary)" }} /></span>
              <div><div className="detail-label">Email</div><div className="detail-val">{customer.email}</div></div>
            </div>
            <div className="detail-row">
              <span className="detail-icon" style={{ display: "inline-flex", alignSelf: "center" }}><MapPin size={16} style={{ color: "var(--primary)" }} /></span>
              <div><div className="detail-label">Address</div><div className="detail-val">{addressDisplay}</div></div>
            </div>
            <div className="detail-row">
              <span className="detail-icon" style={{ display: "inline-flex", alignSelf: "center" }}><Store size={16} style={{ color: "var(--primary)" }} /></span>
              <div><div className="detail-label">Franchise</div><div className="detail-val">{customer.franchise || "Independent"}</div></div>
            </div>
            <div className="detail-row">
              <span className="detail-icon" style={{ display: "inline-flex", alignSelf: "center" }}><Hash size={16} style={{ color: "var(--primary)" }} /></span>
              <div><div className="detail-label">GST Number</div><div className="detail-val mono">{customer.gst || "Not provided"}</div></div>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="detail-card">
          <h3 className="detail-title">Financial Summary</h3>
          <div className="finance-grid">
            <div className="finance-item"><div className="finance-label">Total Paid</div><div className="finance-val green">₹{Number(customer.totalPaid || 0).toLocaleString()}</div></div>
            <div className="finance-item"><div className="finance-label">Credit Due</div><div className={`finance-val ${customer.creditDue > 0 ? "danger" : "green"}`}>₹{Number(customer.creditDue || 0).toLocaleString()}</div></div>
            <div className="finance-item"><div className="finance-label">Total Orders</div><div className="finance-val">{customerOrders.length}</div></div>
            <div className="finance-item"><div className="finance-label">Last Order</div><div className="finance-val">{formatDate(customer.lastOrder) || "Never"}</div></div>
            <div className="finance-item"><div className="finance-label">Avg. Order Gap</div><div className="finance-val">{customer.avgOrderGapDays !== null && customer.avgOrderGapDays !== undefined ? `${customer.avgOrderGapDays} days` : "—"}</div></div>
            {/* <div className="finance-item"><div className="finance-label">Member Since</div><div className="finance-val">{formatDate(customer.createdAt)}</div></div> */}
            <div className="finance-item"><div className="finance-label">Status</div><div className="finance-val"><Badge label={customer.status} /></div></div>
          </div>
        </div>

        {/* Orders History */}
        <div className="detail-card wide-card">
          <h3 className="detail-title">Order History ({customerOrders.length} orders)</h3>
          {customerOrders.length === 0 ? (
            <div className="empty-state small"><div className="empty-icon"><Package size={32} style={{ color: "var(--text-muted)", opacity: 0.6 }} /></div><p>No orders yet</p></div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Invoice No</th>
                    <th>Order Date</th>
                    <th>Unit Price</th>
                    <th>Products</th>
                    <th>Quantity</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {customerOrders.map((o) => (
                    <tr key={o.id} className="clickable-row" onClick={() => navigate(`/orders/${o.id}`)} title="Click to view order details">
                      <td className="mono fw-600 primary-link">#{o.id}</td>
                      <td className="mono text-sm">{o._raw.invoice_number || "—"}</td>
                      <td>{formatDate(o.orderDate) || "—"}</td>
                      <td>{o.products?.map((p, idx) => <div key={idx}>{`${p.price}`}</div>)}</td>
                      <td>{o.products?.map((p, idx) => <div key={idx}>{`${p.name}`}</div>)}</td>
                      <td>{o.products?.map((p, idx) => <div key={idx}>{`${p.qty}`}</div>)}</td>
                      <td className="amount fw-600">₹{Number(o.total || 0).toLocaleString()}</td>
                      <td><Badge label={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="detail-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: 8 }}>
            <h3 className="detail-title" style={{ margin: 0 }}>Activity Timeline</h3>
            {customer.avgOrderGapDays !== null && customer.avgOrderGapDays !== undefined && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#eff6ff", border: "1.5px solid #bfdbfe",
                borderRadius: 20, padding: "4px 12px",
                fontSize: 12, fontWeight: 600, color: "#1d4ed8"
              }}>
                <Package size={12} />
                Avg. order gap: {customer.avgOrderGapDays} days
              </div>
            )}
          </div>
          {timelineData.length === 0 ? (
            <div className="empty-state small">
              <div className="empty-icon"><FileText size={32} style={{ color: "var(--text-muted)", opacity: 0.6 }} /></div>
              <p>No activity logged yet</p>
            </div>
          ) : (
            <div className="timeline">
              {timelineData.map((t, i) => (
                <div key={i} className="timeline-item">
                  <div className="timeline-icon">{getTimelineIcon(t.type)}</div>
                  <div className="timeline-content">
                    <div className="timeline-event">{t.title}</div>
                    <div className="timeline-date">{formatDate(t.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        {customer.notes && (
          <div className="detail-card">
            <h3 className="detail-title">Notes</h3>
            <p className="notes-text">{customer.notes}</p>
          </div>
        )}
      </div>

      <CallModal
        customer={callModal}
        onClose={() => setCallModal(null)}
        onSuccess={fetchCustomerDetails}
      />
    </div>
  );
};

export default CustomerDetail;
